import * as signalR from '@microsoft/signalr';
import { config } from '../config/config';
import authService from './authService';

class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private reconnectInterval = 5000; // 5 seconds
    private onConnectionLost?: () => void;

    async connect(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            console.log('SignalR already connected');
            return;
        }

        // Tear down any lingering (non-connected) connection so we always
        // negotiate fresh — critical when the effective identity has changed
        // (e.g. entering/leaving impersonation), so the hub connects as the
        // impersonated user rather than reusing a stale handshake.
        if (this.connection) {
            try { await this.connection.stop(); } catch { /* ignore */ }
            this.connection = null;
        }

        // Use the effective token (impersonation token when impersonating) so
        // the hub authenticates as the SAME principal as every REST call. Using
        // getAccessToken() here made the hub connect as the host admin during
        // impersonation — wrong tenant/user group, failed negotiate.
        const token = await authService.getEffectiveAccessToken();
        if (!token) {
            console.error('No access token available for SignalR connection');
            throw new Error('Authentication required');
        }

        const apiUrl = config.api.baseUrl; //.replace('/api', ''); // Remove /api suffix  
        const hubUrl = `${apiUrl}/signalr-hubs/notifications`;

        console.log('Connecting to SignalR hub:', hubUrl);

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: async () => {
                    return await authService.getEffectiveAccessToken() || '';
                },
                transport: signalR.HttpTransportType.WebSockets |
                          signalR.HttpTransportType.ServerSentEvents |
                          signalR.HttpTransportType.LongPolling
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalR.LogLevel.Information)
            .build();

        // Detect dead connections within 30 s rather than the default 30 s server-side keepalive window
        this.connection.serverTimeoutInMilliseconds = 30000;

        this.setupEventHandlers();
        
        try {
            await this.connection.start();
            console.log('SignalR Connected Successfully');
            this.reconnectAttempts = 0;
            
            // Join user group after successful connection
            await this.connection.invoke('JoinUserGroup');
            console.log('Joined user group');
        } catch (error) {
            console.error('SignalR Connection Error:', error);
            this.scheduleReconnect();
            throw error;
        }
    }

    private setupEventHandlers(): void {
        if (!this.connection) return;

        this.connection.onclose((error) => {
            console.log('SignalR Connection Closed', error);
            // withAutomaticReconnect exhausted its retry schedule before reaching onclose.
            // Attempt our own manual reconnect cycle; if that also fails, surface the loss.
            if (error) {
                console.warn('SignalR closed with error — attempting manual reconnect');
                this.scheduleReconnect();
            } else {
                // Clean close (intentional disconnect) — notify UI that connection is gone
                this.onConnectionLost?.();
            }
        });

        this.connection.onreconnected((connectionId) => {
            console.log('SignalR Reconnected, connectionId:', connectionId);
            this.reconnectAttempts = 0;
            // Rejoin user group and refresh counts after reconnection
            this.connection?.invoke('JoinUserGroup')
                .then(() => this.connection?.invoke('RefreshCounts'))
                .catch(console.error);
        });

        this.connection.onreconnecting((error) => {
            console.warn('SignalR Reconnecting...', error);
        });
    }

    private scheduleReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached — SignalR connection lost');
            this.onConnectionLost?.();
            return;
        }

        setTimeout(() => {
            this.reconnectAttempts++;
            console.log(`SignalR reconnect attempt ${this.reconnectAttempts}`);
            this.connect().catch(console.error);
        }, this.reconnectInterval * Math.min(this.reconnectAttempts, 5));
    }

    setOnConnectionLost(callback: () => void): void {
        this.onConnectionLost = callback;
    }

    async disconnect(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
                console.log('SignalR disconnected');
            } catch (error) {
                console.error('Error disconnecting SignalR:', error);
            }
            this.connection = null;
        }
    }

    getConnection(): signalR.HubConnection | null {
        return this.connection;
    }

    isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    async invoke(methodName: string, ...args: any[]): Promise<any> {
        if (!this.isConnected()) {
            throw new Error('SignalR is not connected');
        }
        return this.connection!.invoke(methodName, ...args);
    }

    on(eventName: string, callback: (...args: any[]) => void): void {
        if (!this.connection) {
            throw new Error('SignalR connection not initialized');
        }
        this.connection.on(eventName, callback);
    }

    off(eventName: string, callback?: (...args: any[]) => void): void {
        if (!this.connection) return;
        if (callback) {
            this.connection.off(eventName, callback);
        } else {
            this.connection.off(eventName);
        }
    }
}

export const signalRService = new SignalRService();